// EXIF 데이터에서 GPS 정보를 추출하는 유틸리티

interface GPSInfo {
  latitude: number;
  longitude: number;
  address?: string;
}

interface EXIFData {
  [key: string]: any;
  GPSLatitude?: number[];
  GPSLatitudeRef?: string;
  GPSLongitude?: number[];
  GPSLongitudeRef?: string;
}

// EXIF 라이브러리 타입 정의
declare global {
  interface Window {
    EXIF: {
      getData: (file: File, callback: (exifData: EXIFData) => void) => void;
    };
  }
}

/**
 * GPS 좌표를 decimal degrees로 변환
 */
const convertDMSToDD = (
  degrees: number,
  minutes: number,
  seconds: number,
  direction: string
): number => {
  let dd = degrees + minutes / 60 + seconds / 3600;
  if (direction === "S" || direction === "W") {
    dd = dd * -1;
  }
  return dd;
};

/**
 * EXIF GPS 데이터에서 위도/경도 추출
 */
const extractGPSFromEXIF = (exifData: EXIFData): GPSInfo | null => {
  const { GPSLatitude, GPSLatitudeRef, GPSLongitude, GPSLongitudeRef } =
    exifData;

  if (!GPSLatitude || !GPSLongitude || !GPSLatitudeRef || !GPSLongitudeRef) {
    return null;
  }

  try {
    const latitude = convertDMSToDD(
      GPSLatitude[0],
      GPSLatitude[1],
      GPSLatitude[2],
      GPSLatitudeRef
    );

    const longitude = convertDMSToDD(
      GPSLongitude[0],
      GPSLongitude[1],
      GPSLongitude[2],
      GPSLongitudeRef
    );

    return {
      latitude,
      longitude,
    };
  } catch (error) {
    console.error("GPS 좌표 변환 오류:", error);
    return null;
  }
};

/**
 * 이미지 파일에서 GPS 정보 추출 (EXIF.js 사용)
 */
export const extractGPSFromImage = (file: File): Promise<GPSInfo | null> => {
  return new Promise((resolve) => {
    // EXIF 라이브러리가 로드되지 않은 경우
    if (!window.EXIF) {
      console.warn("EXIF 라이브러리가 로드되지 않았습니다.");
      resolve(null);
      return;
    }

    window.EXIF.getData(file, function (this: EXIFData) {
      try {
        const exifData = this;
        const gpsInfo = extractGPSFromEXIF(exifData);
        resolve(gpsInfo);
      } catch (error) {
        console.error("EXIF 데이터 처리 오류:", error);
        resolve(null);
      }
    });
  });
};

/**
 * 브라우저의 File API를 사용한 간단한 EXIF GPS 추출 (EXIF.js 없이)
 */
export const extractGPSFromImageSimple = (
  file: File
): Promise<GPSInfo | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const dataView = new DataView(arrayBuffer);

        // JPEG 파일 확인 (0xFFD8)
        if (dataView.getUint16(0) !== 0xffd8) {
          resolve(null);
          return;
        }

        // EXIF 헤더 찾기
        let offset = 2;
        while (offset < dataView.byteLength) {
          const marker = dataView.getUint16(offset);

          // APP1 마커 (0xFFE1) - EXIF 데이터가 있는 세그먼트
          if (marker === 0xffe1) {
            const exifLength = dataView.getUint16(offset + 2);
            const exifData = new DataView(
              arrayBuffer,
              offset + 4,
              exifLength - 2
            );

            // "Exif\0\0" 문자열 확인
            const exifHeader = String.fromCharCode(
              exifData.getUint8(0),
              exifData.getUint8(1),
              exifData.getUint8(2),
              exifData.getUint8(3)
            );

            if (exifHeader === "Exif") {
              // 간단한 GPS 정보 추출 구현
              // 실제로는 TIFF 헤더 파싱이 필요하지만, 복잡하므로 EXIF.js 라이브러리 사용 권장
              console.log(
                "EXIF 헤더를 찾았지만, 상세 파싱을 위해 EXIF.js 라이브러리를 사용하세요."
              );
              resolve(null);
              return;
            }
          }

          offset += 2 + dataView.getUint16(offset + 2);
        }

        resolve(null);
      } catch (error) {
        console.error("이미지 EXIF 파싱 오류:", error);
        resolve(null);
      }
    };

    reader.onerror = () => {
      console.error("파일 읽기 오류");
      resolve(null);
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * 동영상 파일에서 위치 정보 추출 (제한적)
 * 웹에서는 동영상 메타데이터 추출이 제한적이므로, 서버 사이드 처리 권장
 */
export const extractGPSFromVideo = async (
  file: File
): Promise<GPSInfo | null> => {
  console.warn(
    "동영상에서의 GPS 정보 추출은 웹에서 제한적입니다. 서버 사이드 처리를 권장합니다."
  );

  try {
    // HTML5 Video API를 사용한 기본적인 메타데이터 확인
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);

    return new Promise((resolve) => {
      video.onloadedmetadata = () => {
        // 동영상 메타데이터에서 GPS 정보를 추출하는 것은 웹에서 직접적으로 불가능
        // 추후 서버에서 ffmpeg 등을 사용하여 메타데이터 추출 가능
        console.log("동영상 메타데이터:", {
          duration: video.duration,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
        });

        URL.revokeObjectURL(url);
        resolve(null);
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };

      video.src = url;
    });
  } catch (error) {
    console.error("동영상 메타데이터 추출 오류:", error);
    return null;
  }
};

/**
 * 미디어 파일에서 GPS 정보 추출 (이미지/동영상 자동 판별)
 */
export const extractGPSFromMediaFile = async (
  file: File
): Promise<GPSInfo | null> => {
  const fileType = file.type;

  if (fileType.startsWith("image/")) {
    // EXIF.js가 있으면 사용, 없으면 간단한 방법 사용
    if (window.EXIF) {
      return extractGPSFromImage(file);
    } else {
      return extractGPSFromImageSimple(file);
    }
  } else if (fileType.startsWith("video/")) {
    return extractGPSFromVideo(file);
  } else {
    console.warn("지원되지 않는 파일 형식:", fileType);
    return null;
  }
};
